import { useState, type FormEvent } from 'react';
import { ApiError } from '../../../api/client';
import { publico } from '../../../api/publico';
import { usePedido } from '../../../api/usePedido';
import { Enlace, irA } from '../../../router';
import { useSesion } from '../../../session/Sesion';
import { AvisoError, Boton, Casilla, Entrada, Marco, Selector } from '../../../ui';
import { useFormulario } from '../../../forms/useFormulario';
import {
  LARGOS, requerido, separarNombre, soloDigitos, validarClave, validarEmail, validarMatricula,
  validarNombreCompleto, validarTelefono,
} from '../../../forms/validacion';
import { CampoClave, MedidorDeFuerza } from './piezas';

export function RegistroProfesional() {
  const { ingresar } = useSesion();
  const { datos: catalogos } = usePedido(() => publico.catalogos(), []);
  const [declara, setDeclara] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const formulario = useFormulario(
    { nombre: '', email: '', matricula: '', especialidad: '', barrio: '', telefono: '', direccion: '', clave: '', repetir: '' },
    {
      nombre: validarNombreCompleto, email: validarEmail, matricula: validarMatricula,
      especialidad: requerido('La especialidad'), telefono: validarTelefono, clave: validarClave,
      repetir: (v, todos) => (!v ? 'Repetí la contraseña.' : v !== todos.clave ? 'Las contraseñas no coinciden.' : null),
    },
    { nombre: LARGOS.nombre, email: LARGOS.email, matricula: LARGOS.matricula, direccion: LARGOS.direccion, clave: LARGOS.clave, repetir: LARGOS.clave },
  );
  const { valores } = formulario;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!formulario.validarTodo()) return;
    if (!declara) {
      setError('Tenés que declarar que los datos de tu matrícula son verídicos.');
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      await publico.registrarProfesional({
        ...separarNombre(valores.nombre), email: valores.email.trim(), matricula: valores.matricula.trim().toUpperCase(),
        especialidad: valores.especialidad, barrio: valores.barrio || undefined, telefono: valores.telefono,
        direccion: valores.direccion.trim() || undefined, clave: valores.clave, acepta_declaracion: true,
      });
      await ingresar(valores.email.trim(), valores.clave);
      irA('/pro/perfil', { reemplazar: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos crear la cuenta. Probá de nuevo en un momento.');
      setEnviando(false);
    }
  };

  return (
    <Marco
      titulo="Sumá tu consultorio"
      bajada="Empezás gratis. Verificamos tu matrícula y tu perfil aparece en las búsquedas."
      pie={<>¿Ya tenés cuenta? <Enlace a="/login">Entrá</Enlace></>}
    >
      <form onSubmit={(e) => void enviar(e)} noValidate className="acceso__formulario">
        <AvisoError>{error}</AvisoError>
        <Entrada etiqueta="Nombre y apellido" autoComplete="name" required {...formulario.campo('nombre')} />
        <Entrada etiqueta="Correo electrónico" type="email" autoComplete="email" required {...formulario.campo('email')} />
        <div className="acceso__fila">
          <Entrada etiqueta="Matrícula" placeholder="MN 145.234" required {...formulario.campo('matricula')} />
          <Selector etiqueta="Especialidad" marcador="Elegí una" required
            opciones={catalogos?.especialidades ?? []} {...formulario.campo('especialidad')} />
        </div>
        <div className="acceso__fila">
          <Selector etiqueta="Barrio del consultorio" marcador="Opcional"
            opciones={catalogos?.barrios ?? []} {...formulario.campo('barrio')} />
          <Entrada etiqueta="Teléfono" inputMode="tel" required {...formulario.campo('telefono')}
            onChange={(e) => formulario.cambiar('telefono', soloDigitos(e.target.value, LARGOS.telefono))} />
        </div>
        <Entrada etiqueta="Dirección del consultorio" placeholder="Opcional" {...formulario.campo('direccion')} />
        <div>
          <CampoClave etiqueta="Contraseña" autoComplete="new-password" required
            ayuda="Al menos 8 caracteres, con letras y números." {...formulario.campo('clave')} />
          <MedidorDeFuerza clave={valores.clave} />
        </div>
        <CampoClave etiqueta="Repetir contraseña" autoComplete="new-password" required {...formulario.campo('repetir')} />
        <Casilla checked={declara} onChange={(e) => setDeclara(e.target.checked)}
          etiqueta="Declaro que los datos de mi matrícula son verídicos" />
        <Boton type="submit" tamano="grande" anchoCompleto cargando={enviando}>Crear cuenta profesional</Boton>
      </form>
    </Marco>
  );
}
